import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { SERVICE_CITIES, COMPANY } from "../lib/constants";

export function Eyebrow({ children }) {
  return <div className="eyebrow mb-3">{children}</div>;
}

export function SectionHeading({ eyebrow, title, subtitle, center, light }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"} mb-12`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight ${light ? "text-white" : "text-charcoal-900"}`}>{title}</h2>
      {subtitle && <p className={`mt-4 text-base leading-relaxed ${light ? "text-gray-300" : "text-charcoal-600"}`}>{subtitle}</p>}
    </div>
  );
}

export const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export function CTABand() {
  return (
    <section className="grain-dark" data-testid="cta-band">
      <div className="ubm-container py-20 text-center">
        <Eyebrow>Ready when you are</Eyebrow>
        <h2 className="text-3xl sm:text-4xl font-bold text-white max-w-2xl mx-auto">
          Let's keep your space spotless — commercial or residential.
        </h2>
        <p className="text-gray-300 mt-4 max-w-xl mx-auto">
          Request a free commercial quote or book your residential cleaning online in minutes.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/commercial#quote" className="bg-white text-charcoal-900 hover:bg-gray-100 font-semibold px-7 py-3.5 rounded-md transition-colors" data-testid="cta-quote">
            Request a Commercial Quote
          </Link>
          <Link to="/book" className="bg-primary text-white hover:bg-primary-hover font-semibold px-7 py-3.5 rounded-md transition-colors" data-testid="cta-book">
            Book a Residential Cleaning
          </Link>
        </div>
      </div>
    </section>
  );
}

export function ServiceAreaSection() {
  return (
    <section className="bg-white" data-testid="service-area">
      <div className="ubm-container py-20">
        <SectionHeading eyebrow="Service Area" title={`Proudly serving ${COMPANY.region}`}
          subtitle="We service homes and businesses across the region. Only the cities we currently confirm coverage for are listed below." />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {SERVICE_CITIES.map((c, i) => (
            <motion.div key={c} {...fadeUp} transition={{ delay: i * 0.03 }}
              className="flex items-center gap-2 border border-gray-200 rounded-md px-4 py-3 hover:border-primary transition-colors">
              <MapPin size={16} className="text-primary shrink-0" />
              <span className="text-sm font-medium text-charcoal-800">{c}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PageHero({ eyebrow, title, subtitle, image, children }) {
  return (
    <section className="relative grain-dark overflow-hidden" data-testid="page-hero">
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-charcoal-900/70" />
        </div>
      )}
      <div className="relative ubm-container py-20 sm:py-28">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
          {eyebrow && <div className="eyebrow mb-3">{eyebrow}</div>}
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">{title}</h1>
          {subtitle && <p className="mt-5 text-lg text-gray-300 leading-relaxed">{subtitle}</p>}
          {children && <div className="mt-8">{children}</div>}
        </motion.div>
      </div>
    </section>
  );
}
